import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';

import { ChatExportStatus, ChatExportTask } from '../models/chat-export.model';

@Component({
  selector: 'servicenumber-chat-export-list',
  standalone: true,
  imports: [CommonModule, NzTableModule, NzButtonModule, NzIconModule, NzTagModule, I18nPipe, TestIdDirective],
  templateUrl: './chat-export-list.component.html',
  styleUrls: ['./chat-export-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatExportListComponent {
  readonly ChatExportStatus = ChatExportStatus;

  @Input() records: ChatExportTask[] = [];
  @Input() loading: boolean = false;

  @Output() readonly download = new EventEmitter<ChatExportTask>();
  @Output() readonly detail = new EventEmitter<ChatExportTask>();
  @Output() readonly delete = new EventEmitter<ChatExportTask>();
  @Output() readonly refresh = new EventEmitter<void>();

  onDownload(task: ChatExportTask): void {
    if (task.status === ChatExportStatus.READY && !task.isDeleting) {
      this.download.emit(task);
    }
  }

  onDetail(task: ChatExportTask): void {
    this.detail.emit(task);
  }

  onDelete(task: ChatExportTask): void {
    this.delete.emit(task);
  }

  onRefresh(): void {
    this.refresh.emit();
  }

  getStatusTagColor(status: ChatExportStatus): string {
    switch (status) {
      case ChatExportStatus.READY:
        return 'success';
      case ChatExportStatus.PROCESSING:
        return 'processing';
      case ChatExportStatus.FAILED:
        return 'error';
      case ChatExportStatus.EXPIRED:
      default:
        return 'default';
    }
  }

  getStatusTextKey(status: ChatExportStatus): string {
    switch (status) {
      case ChatExportStatus.READY:
        return 'servicenumber.chat-export.status.ready';
      case ChatExportStatus.PROCESSING:
        return 'servicenumber.chat-export.status.processing';
      case ChatExportStatus.FAILED:
        return 'servicenumber.chat-export.status.failed';
      case ChatExportStatus.EXPIRED:
      default:
        return 'servicenumber.chat-export.status.expired';
    }
  }

  getDeleteBtnTextKey(task: ChatExportTask): string {
    if (task.cleanupError) {
      return 'servicenumber.chat-export.retry-delete-btn';
    }
    if (task.isDeleting) {
      return 'servicenumber.chat-export.deleting-btn';
    }
    if (task.status === ChatExportStatus.EXPIRED) {
      return 'servicenumber.chat-export.delete-record-btn';
    }
    return 'servicenumber.chat-export.delete-btn';
  }

  formatFileSize(bytes?: number): string {
    if (bytes === undefined || bytes === null || bytes <= 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  getShortTaskId(id: string): string {
    if (!id) return '';
    return id.length > 8 ? id.substring(0, 8) : id;
  }

  formatOwnerName(name?: string): string {
    if (!name) return '管理員';
    const trimmed = name.trim();
    if (/^[0-9a-fA-F]{24}$/.test(trimmed)) {
      return '管理員';
    }
    return trimmed;
  }
}
